async function postForm(endpoint, formData) {
	const rawToken = window.frappe?.csrf_token;
	const csrfToken = (rawToken && rawToken !== "None" && rawToken !== "null") ? rawToken : "";
	const headers = csrfToken ? { "X-Frappe-CSRF-Token": csrfToken } : {};

	const response = await fetch(endpoint, {
		method: "POST",
		headers,
		body: formData,
		credentials: "same-origin",
	});
	const result = await response.json();
	if (!response.ok || result.exc) {
		const message = result._server_messages
			? JSON.parse(result._server_messages).map((item) => JSON.parse(item).message).join(" ")
			: result.message;
		throw new Error(message || "Unable to complete this request.");
	}
	return result.message || result;
}

function mountStandaloneApplication(root) {
	const form = root.querySelector("#career-application-form");
	if (!form) return;
	const fileInput = form.querySelector("input[type='file']");
	const title = form.querySelector("[data-attachment-title]");
	const description = form.querySelector("[data-attachment-description]");
	const remove = form.querySelector("[data-remove-attachment]");
	const submit = form.querySelector("button[type='submit']");
	const status = form.querySelector("[data-career-status]");
	fileInput?.addEventListener("change", () => {
		const file = fileInput.files?.[0];
		if (!file) return;
		title.textContent = file.name;
		description.textContent = `${file.name.split(".").pop().toUpperCase()} · ${(file.size / 1024 / 1024).toFixed(2)} MB · Ready to upload`;
		form.querySelector("[data-career-attachment]")?.classList.add("has-file");
	});
	remove?.addEventListener("click", (event) => {
		event.preventDefault();
		fileInput.value = "";
		title.textContent = "Choose your resume";
		description.textContent = "PDF, DOC or DOCX · maximum 5 MB";
		form.querySelector("[data-career-attachment]")?.classList.remove("has-file");
	});
	form.addEventListener("submit", async (event) => {
		event.preventDefault();
		submit.disabled = true;
		submit.textContent = "Uploading and submitting…";
		status.className = "careers-status-msg is-pending";
		status.textContent = "Uploading your resume securely…";
		try {
			await postForm("/api/method/solufy_website.api.careers.submit_application", new FormData(form));
			form.reset();
			status.className = "careers-status-msg is-success";
			status.textContent = "Application submitted successfully. Our recruitment team will review it shortly.";
			submit.textContent = "Application submitted ✓";
		} catch (error) {
			status.className = "careers-status-msg is-error";
			status.textContent = error.message;
			submit.disabled = false;
			submit.textContent = "Submit application →";
		}
	});
}

function mountCareerSearch(root) {
	const searchInput = root.querySelector("[data-career-search-input]");
	const list = root.querySelector(".career-list");
	if (!searchInput || !list) return;

	const clearBtn = root.querySelector("[data-career-search-clear]");
	const countDisplay = root.querySelector("[data-career-count-display]");
	const noResults = root.querySelector("[data-career-no-results]");
	const searchTermSpan = root.querySelector("[data-career-search-term]");
	const resetBtn = root.querySelector("[data-career-reset-search]");
	const originalCards = [...list.querySelectorAll(".career-list-card")];
	const total = originalCards.length;

	function filterJobs() {
		const rawQuery = searchInput.value.trim().toLowerCase();
		const keywords = rawQuery.split(/\s+/).filter(Boolean);

		if (clearBtn) {
			clearBtn.hidden = !rawQuery;
		}

		const nav = list.querySelector(".solufy-pagination");

		if (!rawQuery) {
			// Restore original order when search query is empty
			originalCards.forEach((card) => {
				delete card.dataset.filterHidden;
				card.removeAttribute("data-filter-hidden");
				if (nav) {
					list.insertBefore(card, nav);
				} else {
					list.appendChild(card);
				}
			});

			if (countDisplay) {
				countDisplay.textContent = `Showing ${total} open ${total === 1 ? "role" : "roles"}`;
			}
			if (noResults) {
				noResults.hidden = true;
			}
			list.dispatchEvent(new CustomEvent("solufy:filter"));
			return;
		}

		// Calculate match score and filter
		const matches = [];
		const nonMatches = [];

		originalCards.forEach((card) => {
			const title = (card.dataset.title || "").toLowerCase();
			const dept = (card.dataset.department || "").toLowerCase();
			const loc = (card.dataset.location || "").toLowerCase();
			const type = (card.dataset.type || "").toLowerCase();
			const kw = (card.dataset.keywords || "").toLowerCase();
			const text = [title, dept, loc, type, kw, card.textContent || ""].join(" ").toLowerCase();

			const isMatch = keywords.every((term) => text.includes(term));
			if (!isMatch) {
				card.dataset.filterHidden = "true";
				card.setAttribute("data-filter-hidden", "true");
				nonMatches.push(card);
				return;
			}

			delete card.dataset.filterHidden;
			card.removeAttribute("data-filter-hidden");

			// Score match for relevance: title match highest, then dept/loc, then description
			let score = 0;
			if (title === rawQuery) score += 100;
			else if (title.startsWith(rawQuery)) score += 60;
			else if (title.includes(rawQuery)) score += 40;

			keywords.forEach((k) => {
				if (title.includes(k)) score += 20;
				if (dept.includes(k) || loc.includes(k) || type.includes(k)) score += 10;
				if (kw.includes(k)) score += 5;
			});

			matches.push({ card, score });
		});

		// Sort matching cards by score descending so the best match comes first
		matches.sort((a, b) => b.score - a.score);

		// Reorder matching cards to the top of list
		matches.forEach(({ card }) => {
			if (nav) {
				list.insertBefore(card, nav);
			} else {
				list.appendChild(card);
			}
		});
		nonMatches.forEach((card) => {
			if (nav) {
				list.insertBefore(card, nav);
			} else {
				list.appendChild(card);
			}
		});

		const matchCount = matches.length;
		if (countDisplay) {
			countDisplay.textContent = `Showing ${matchCount} of ${total} ${total === 1 ? "role" : "roles"}`;
		}

		if (noResults) {
			noResults.hidden = matchCount > 0;
			if (searchTermSpan) {
				searchTermSpan.textContent = rawQuery;
			}
		}

		list.dispatchEvent(new CustomEvent("solufy:filter"));
	}

	searchInput.addEventListener("input", filterJobs);

	clearBtn?.addEventListener("click", () => {
		searchInput.value = "";
		filterJobs();
		searchInput.focus();
	});

	resetBtn?.addEventListener("click", () => {
		searchInput.value = "";
		filterJobs();
		searchInput.focus();
	});
}

function mountCareerAlertToggle(root) {
	const toggle = root.querySelector("[data-toggle-career-alerts]");
	const panel = root.querySelector("[data-career-alert-panel]");
	if (!toggle || !panel) return;
	toggle.addEventListener("click", () => {
		const expanded = toggle.getAttribute("aria-expanded") !== "true";
		panel.hidden = !expanded;
		toggle.setAttribute("aria-expanded", String(expanded));
		toggle.textContent = expanded ? "Hide job alert form" : "Get job alerts";
		if (expanded) {
			panel.scrollIntoView({ behavior: "smooth", block: "start" });
			panel.querySelector("input[name='full_name']")?.focus({ preventScroll: true });
		}
	});
}

function mountCareerAlerts(root) {
	const forms = root.querySelectorAll("#career-alert-form, [data-career-alert-form]");
	if (!forms.length) return;
	forms.forEach((form) => {
		const button = form.querySelector("button[type='submit']");
		const status = form.querySelector("[data-career-alert-status]");
		form.addEventListener("submit", async (event) => {
			event.preventDefault();
			if (!button) return;
			const originalText = button.textContent;
			button.disabled = true;
			button.textContent = "Registering…";
			if (status) {
				status.className = "careers-status-msg is-pending";
				status.textContent = "Saving your preferences securely…";
			}
			try {
				const result = await postForm("/api/method/solufy_website.api.careers.register_job_alert", new FormData(form));
				form.reset();
				if (status) {
					status.className = "careers-status-msg is-success";
					status.textContent = result.message || (result.already_registered
						? "Your preferences have been updated. We’ll notify you by email for matching roles."
						: "Registered successfully! We've sent a confirmation email.");
				}
				button.textContent = "Registered ✓";
				setTimeout(() => {
					button.disabled = false;
					button.textContent = originalText;
				}, 6000);
			} catch (error) {
				if (status) {
					status.className = "careers-status-msg is-error";
					status.textContent = error.message;
				}
				button.disabled = false;
				button.textContent = originalText;
			}
		});
	});
}

export function mountCareersApplication(root = document) {
	mountCareerSearch(root);
	mountCareerAlertToggle(root);
	mountCareerAlerts(root);
	mountStandaloneApplication(root);
	const modal = root.querySelector("#careers-application-modal");
	if (!modal) return;
	const form = modal.querySelector("#careers-application-form");
	const roleSelect = modal.querySelector("#career-target-role");
	const jobInput = modal.querySelector("#career-job-opening");
	const expInput = modal.querySelector("#career-experience-level");
	const status = modal.querySelector("[data-career-status]");
	const submit = form.querySelector("button[type='submit']");

	function openModal(opening) {
		roleSelect.value = opening;
		jobInput.value = opening;
		modal.classList.add("is-active");
		modal.setAttribute("aria-hidden", "false");
		document.body.style.overflow = "hidden";
		setTimeout(() => form.querySelector("input:not([type='hidden'])")?.focus(), 150);
	}
	function closeModal() {
		modal.classList.remove("is-active");
		modal.setAttribute("aria-hidden", "true");
		document.body.style.overflow = "";
		status.textContent = "";
		status.className = "careers-status-msg";
	}
	root.querySelectorAll("[data-open-career-modal]").forEach((button) => button.addEventListener("click", () => openModal(button.dataset.jobOpening)));
	modal.querySelectorAll("[data-close-careers-modal]").forEach((button) => button.addEventListener("click", closeModal));
	modal.addEventListener("click", (event) => { if (event.target === modal) closeModal(); });
	document.addEventListener("keydown", (event) => { if (event.key === "Escape" && modal.classList.contains("is-active")) closeModal(); });
	roleSelect.addEventListener("change", () => { jobInput.value = roleSelect.value; });
	modal.querySelectorAll(".exp-pill-btn").forEach((pill) => pill.addEventListener("click", () => {
		modal.querySelectorAll(".exp-pill-btn").forEach((item) => item.classList.remove("is-selected"));
		pill.classList.add("is-selected");
		expInput.value = pill.dataset.val;
	}));
	form.addEventListener("submit", async (event) => {
		event.preventDefault();
		submit.disabled = true;
		submit.textContent = "Submitting…";
		status.className = "careers-status-msg is-pending";
		status.textContent = "Saving your application…";
		try {
			await postForm("/api/method/solufy_website.api.careers.submit_application", new FormData(form));
			form.reset();
			status.className = "careers-status-msg is-success";
			status.textContent = "Application submitted. Our recruitment team will review it and contact you if your profile is shortlisted.";
			submit.textContent = "Application sent ✓";
			setTimeout(closeModal, 3200);
		} catch (error) {
			status.className = "careers-status-msg is-error";
			status.textContent = error.message;
			submit.disabled = false;
			submit.textContent = "Submit application →";
		}
	});
}
